/** AgentConfigEditor — form for editing agent configuration; version history + publish.
 *
 * The one genuinely complex multi-field form in the settings surface: real
 * cross-field validation (required fields, numeric range, at-least-one-item
 * lists) gated behind two different submit actions (Save Draft / Publish).
 * That complexity is what earns react-hook-form + zod here — every other
 * settings form in this codebase stays on plain useState (see
 * AddCoOwnerForm.tsx for that simpler pattern).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useParams } from 'react-router-dom';
import { CircleHelp } from 'lucide-react';
import { toast } from '@/shared/ui/sonner';
import { getUserErrorMessage } from '@/shared/api';

import { useConfirmedAction } from './useConfirmedAction';
import { Button } from '@/shared/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/form';
import { Input } from '@/shared/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/shared/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Switch } from '@/shared/ui/switch';
import { Textarea } from '@/shared/ui/textarea';

import { useAuth } from '../../../contexts/AuthContext';
import {
  getActiveConfig,
  listConfigVersions,
  listConfigModels,
  publishConfig,
  saveConfigDraft,
  activateConfigVersion,
  deleteConfigVersion,
  type BedrockModelOption,
  type ConfigVersion,
  type ModelProviderId,
} from '../../../services/agentHub';
import type { AgentAccountType, AgentDevStatus, AgentView } from '@/entities/agent';
import { readEnv } from '@/shared/config/env';
import VersionHistorySidebar from './VersionHistorySidebar';
import PublishConfirmDialog from './PublishConfirmDialog';
import ListField from './ListField';

// ── Platform constraints (must mirror backend manifest schema) ──────────
const MAX_ITERATIONS_CAP = 100;
const MIN_ITERATIONS = 1;
const ITERATIONS_RANGE_MESSAGE = `Must be ${MIN_ITERATIONS}–${MAX_ITERATIONS_CAP}`;

const MODEL_PROVIDER_OPTIONS: ReadonlyArray<{ id: ModelProviderId; label: string }> = [
  { id: 'azure_foundry', label: 'Azure Foundry' },
  { id: 'bedrock', label: 'Amazon Bedrock' },
];

const EFFORT_OPTIONS = [
  { id: 'low', label: 'Low — fastest, shallow reasoning' },
  { id: 'medium', label: 'Medium — balanced (default)' },
  { id: 'high', label: 'High — slower, deeper reasoning' },
  { id: 'xhigh', label: 'Extra high — very slow ⚠︎ pending backend support' },
  { id: 'max', label: 'Max — deepest, highest latency' },
] as const;

const ACCOUNT_TYPE_OPTIONS: ReadonlyArray<{ id: AgentAccountType; label: string }> = [
  { id: 'single', label: 'Single Account Deep-Dive' },
  { id: 'cross', label: 'Cross Account Analysis' },
  { id: 'solution', label: 'Solution Level Insights' },
  { id: 'orchestrator', label: 'Orchestrator' },
];

const DEV_STATUS_OPTIONS: ReadonlyArray<{ id: AgentDevStatus; label: string }> = [
  { id: 'live', label: 'Live' },
  { id: 'experimental', label: 'Experimental' },
  { id: 'development', label: 'Development' },
  { id: 'planned', label: 'Planned' },
];

const ACCOUNT_TYPE_VALUES = ACCOUNT_TYPE_OPTIONS.map((o) => o.id) as [
  AgentAccountType,
  ...AgentAccountType[],
];
const DEV_STATUS_VALUES = DEV_STATUS_OPTIONS.map((o) => o.id) as [
  AgentDevStatus,
  ...AgentDevStatus[],
];

function toAccountType(value: unknown, fallback: AgentAccountType): AgentAccountType {
  return ACCOUNT_TYPE_OPTIONS.some((option) => option.id === value)
    ? (value as AgentAccountType)
    : fallback;
}

function toDevStatus(value: unknown, fallback: AgentDevStatus): AgentDevStatus {
  return DEV_STATUS_OPTIONS.some((option) => option.id === value)
    ? (value as AgentDevStatus)
    : fallback;
}

// ── Form schema ───────────────────────────────────────────────────────────
const MODEL_PROVIDER_VALUES = MODEL_PROVIDER_OPTIONS.map((o) => o.id) as [
  ModelProviderId,
  ...ModelProviderId[],
];

const configFormSchema = z.object({
  agentName: z.string().trim().min(1, 'Agent name is required'),
  description: z.string().trim().min(1, 'Description is required'),
  examplePrompts: z.array(z.string()).min(1, 'Add at least one example prompt'),
  bullets: z.array(z.string()).min(1, 'Add at least one bullet'),
  tags: z.string(),
  modelProvider: z.enum(MODEL_PROVIDER_VALUES),
  modelId: z.string().min(1, 'Model is required'),
  maxIterations: z
    .number()
    .min(MIN_ITERATIONS, ITERATIONS_RANGE_MESSAGE)
    .max(MAX_ITERATIONS_CAP, ITERATIONS_RANGE_MESSAGE),
  effort: z.string().min(1, 'Effort is required'),
  accountType: z.enum(ACCOUNT_TYPE_VALUES),
  devStatus: z.enum(DEV_STATUS_VALUES),
  useDbOverlay: z.boolean(),
  changeSummary: z.string(),
});

type ConfigFormValues = z.infer<typeof configFormSchema>;

const DEFAULT_VALUES: ConfigFormValues = {
  agentName: '',
  description: '',
  examplePrompts: [],
  bullets: [],
  tags: '',
  modelProvider: 'azure_foundry',
  modelId: '',
  maxIterations: 10,
  effort: 'medium',
  accountType: 'single',
  devStatus: 'live',
  useDbOverlay: false,
  changeSummary: '',
};

/** Maps a stored config payload onto form values (everything but changeSummary,
 * which is independent of the loaded version — see callers). */
function mapPayloadToFormValues(
  payload: Record<string, unknown>,
  fallbackAccountType: AgentAccountType,
  fallbackDevStatus: AgentDevStatus,
): Omit<ConfigFormValues, 'changeSummary'> {
  const manifest = (payload.manifest ?? {}) as Record<string, unknown>;

  let modelId = '';
  let modelProvider: ModelProviderId = 'azure_foundry';
  if (typeof manifest.model === 'string') {
    modelId = manifest.model;
  } else if (manifest.model && typeof manifest.model === 'object') {
    const modelObj = manifest.model as Record<string, unknown>;
    modelId = (modelObj.id as string) || (modelObj.name as string) || '';
    if (modelObj.provider === 'bedrock' || modelObj.provider === 'azure_foundry') {
      modelProvider = modelObj.provider;
    }
  }

  return {
    agentName: (manifest.name as string) ?? '',
    description: (manifest.description as string) ?? '',
    examplePrompts: (payload.example_prompts as string[] | undefined) ?? [],
    bullets: (manifest.bullets as string[] | undefined) ?? [],
    tags: ((manifest.tags as string[]) ?? []).join(', '),
    modelProvider,
    modelId,
    maxIterations:
      typeof manifest.max_iterations === 'number' ? (manifest.max_iterations as number) : 10,
    effort: (manifest.effort as string) ?? 'medium',
    accountType: toAccountType(manifest.account_type, fallbackAccountType),
    devStatus: toDevStatus(manifest.dev_status, fallbackDevStatus),
    useDbOverlay: manifest.use_db_overlay === true,
  };
}

function buildPayload(values: ConfigFormValues, isLocal: boolean): Record<string, unknown> {
  return {
    example_prompts: values.examplePrompts,
    manifest: {
      name: values.agentName,
      description: values.description,
      tags: values.tags.split(',').map((t) => t.trim()).filter(Boolean),
      ...(isLocal ? { use_db_overlay: values.useDbOverlay } : {}),
      account_type: values.accountType,
      dev_status: values.devStatus,
      bullets: values.bullets,
      max_iterations: values.maxIterations,
      effort: values.effort,
      model: { name: values.modelId, provider: values.modelProvider },
    },
  };
}

const requiredLabelClass = "after:ml-0.5 after:text-destructive after:content-['*']";

function FieldHelp({ title, description }: { title: string; description: string }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={`${title} help`}
          className="text-muted-foreground"
        >
          <CircleHelp />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64">
        <PopoverHeader>
          <PopoverTitle>{title}</PopoverTitle>
          <PopoverDescription>{description}</PopoverDescription>
        </PopoverHeader>
      </PopoverContent>
    </Popover>
  );
}

function FieldLabelRow({
  label,
  required,
  help,
}: {
  label: string;
  required?: boolean;
  help: { title: string; description: string };
}) {
  return (
    <div className="flex items-center gap-1">
      <FormLabel className={required ? requiredLabelClass : undefined}>{label}</FormLabel>
      <FieldHelp title={help.title} description={help.description} />
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────

export default function AgentConfigEditor() {
  const { agentId = '' } = useParams<{ agentId: string }>();
  const { agents, capabilities } = useAuth();
  const isAdmin = capabilities?.is_admin === true;
  const appEnv = readEnv('VITE_APP_ENV');
  const isLocal = appEnv !== undefined ? appEnv === 'local' : import.meta.env.DEV;

  const [versions, setVersions] = useState<ConfigVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [activeVersionNumber, setActiveVersionNumber] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modelOptions, setModelOptions] = useState<BedrockModelOption[]>([]);
  const [modelOptionsLoaded, setModelOptionsLoaded] = useState(false);
  const [loadingModelOptions, setLoadingModelOptions] = useState(false);
  const [modelOptionsError, setModelOptionsError] = useState<string | null>(null);

  const form = useForm<ConfigFormValues>({
    resolver: zodResolver(configFormSchema),
    defaultValues: DEFAULT_VALUES,
  });
  const { control, reset, getValues, setValue, handleSubmit, watch, formState } = form;

  const currentAgent = useMemo<AgentView | undefined>(
    () => agents.find((agent) => agent.id === agentId),
    [agents, agentId],
  );
  const isOrchestrator =
    agentId === 'argus' ||
    currentAgent?.runtime === 'orchestrator' ||
    currentAgent?.accountType === 'orchestrator';

  const modelProvider = watch('modelProvider');
  const modelId = watch('modelId');
  const effort = watch('effort');

  const providerLabel = useMemo(
    () => MODEL_PROVIDER_OPTIONS.find((o) => o.id === modelProvider)?.label ?? modelProvider,
    [modelProvider],
  );

  const availableModelOptions = useMemo(() => {
    if (!modelId || modelOptions.some((option) => option.model_id === modelId)) {
      return modelOptions;
    }
    return [
      {
        model_id: modelId,
        display_name: modelId,
        provider_name: 'Anthropic',
        region: 'current',
        lifecycle_status: 'CURRENT',
      },
      ...modelOptions,
    ];
  }, [modelId, modelOptions]);

  const loadModelOptions = useCallback(async () => {
    if (!agentId) return;
    setLoadingModelOptions(true);
    setModelOptionsError(null);
    try {
      const models = await listConfigModels(agentId, modelProvider);
      setModelOptions(models);
      setModelOptionsLoaded(true);
    } catch (err) {
      setModelOptionsError(getUserErrorMessage(err));
    } finally {
      setLoadingModelOptions(false);
    }
  }, [agentId, modelProvider]);

  const ensureModelOptionsLoaded = useCallback(() => {
    if (modelOptionsLoaded || loadingModelOptions) return;
    void loadModelOptions();
  }, [modelOptionsLoaded, loadingModelOptions, loadModelOptions]);

  const handleProviderChange = useCallback((next: ModelProviderId) => {
    setValue('modelProvider', next);
    // The model list and the selected model_id are provider-scoped — switching
    // providers invalidates both, so force a refetch on next picker open.
    setValue('modelId', '');
    setModelOptions([]);
    setModelOptionsLoaded(false);
    setModelOptionsError(null);
  }, [setValue]);

  const loadData = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const [active, versionList] = await Promise.all([
        getActiveConfig(agentId),
        listConfigVersions(agentId),
      ]);
      setVersions(versionList);
      if (active) {
        setSelectedVersionId(active.id);
        setActiveVersionNumber(active.version_number ?? 0);
        reset({
          ...mapPayloadToFormValues(
            active.payload,
            currentAgent?.accountType ?? 'single',
            currentAgent?.devStatus ?? 'live',
          ),
          changeSummary: getValues('changeSummary'),
        });
      } else {
        setActiveVersionNumber(0);
      }
    } catch (err) {
      setError(getUserErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [agentId, reset, getValues, currentAgent?.accountType, currentAgent?.devStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showHighEffortWarning = effort === 'high' || effort === 'xhigh' || effort === 'max';

  const handleSaveDraft = handleSubmit(async (values) => {
    if (!agentId) return;
    setSavingDraft(true);
    setError(null);
    try {
      const payload = buildPayload(values, isLocal);
      await saveConfigDraft(agentId, payload, values.changeSummary || undefined);
      setValue('changeSummary', '');
      toast.success('Config draft saved — test it in Experiment tab');
      await loadData();
    } catch (err) {
      setError(getUserErrorMessage(err));
    } finally {
      setSavingDraft(false);
    }
  });

  const publish = useConfirmedAction(
    async () => {
      const values = getValues();
      const payload = buildPayload(values, isLocal);
      await publishConfig(agentId, payload, values.changeSummary || undefined);
      setValue('changeSummary', '');
      toast.success('Configuration published');
      await loadData();
    },
    (err) => setError(getUserErrorMessage(err)),
  );

  const onPublishClick = handleSubmit(() => {
    publish.trigger();
  });

  const [activatingId, setActivatingId] = useState<string | null>(null);

  const handleActivate = async (versionId: string) => {
    if (!agentId) return;
    setActivatingId(versionId);
    setError(null);
    try {
      await activateConfigVersion(agentId, versionId);
      toast.success('Version activated');
      await loadData();
    } catch (err) {
      setError(getUserErrorMessage(err));
    } finally {
      setActivatingId(null);
    }
  };

  const deleteTargetId = useRef<string | null>(null);

  const deleteVersion = useConfirmedAction(
    async () => {
      if (!deleteTargetId.current) return;
      await deleteConfigVersion(agentId, deleteTargetId.current);
      toast.success('Draft deleted');
      await loadData();
    },
    (err) => setError(getUserErrorMessage(err)),
  );

  const handleDeleteRequest = (versionId: string) => {
    deleteTargetId.current = versionId;
    deleteVersion.trigger();
  };

  const handleVersionSelect = (versionId: string) => {
    setSelectedVersionId(versionId);
    const version = versions.find((v) => v.id === versionId);
    if (version) {
      reset({
        ...mapPayloadToFormValues(
          version.payload,
          currentAgent?.accountType ?? 'single',
          currentAgent?.devStatus ?? 'live',
        ),
        changeSummary: getValues('changeSummary'),
      });
    }
  };

  const hasErrors = formState.isSubmitted && Object.keys(formState.errors).length > 0;

  return (
    <div className="flex flex-col gap-4 pt-4">
      {error && <p className="p-3 text-sm text-destructive">{error}</p>}

      {loading ? (
        <p className="text-sm">Loading...</p>
      ) : (
        <div className="flex items-start gap-4">
          <Form {...form}>
            <div className="flex min-w-0 grow flex-col gap-3">
              <p className="text-sm text-muted-foreground">
                {activeVersionNumber > 0
                  ? `Configuration v${activeVersionNumber}`
                  : 'Draft — unsaved, from manifest'}
              </p>

              {/* Row 1: Agent Name */}
              <FormField
                control={control}
                name="agentName"
                render={({ field }) => (
                  <FormItem>
                    <FieldLabelRow
                      label="Agent Name"
                      required
                      help={{
                        title: 'Agent Name',
                        description: 'Shown on the agent card and in the agent selector dropdown.',
                      }}
                    />
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Row 2: Provider | Model | Effort */}
              {!isOrchestrator && <div className="flex items-end gap-4">
                <FormField
                  control={control}
                  name="modelProvider"
                  render={({ field }) => (
                    <FormItem className="flex-1 basis-0">
                      <FieldLabelRow
                        label="Provider"
                        required
                        help={{
                          title: 'Provider',
                          description:
                            'The Claude hosting platform this agent runs on. Switching providers reloads the model list below.',
                        }}
                      />
                      <Select
                        value={field.value}
                        onValueChange={(next) => handleProviderChange(next as ModelProviderId)}
                      >
                        <FormControl>
                          <SelectTrigger aria-label="Provider" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {MODEL_PROVIDER_OPTIONS.map((o) => (
                            <SelectItem key={o.id} value={o.id}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="modelId"
                  render={({ field }) => (
                    <FormItem className="min-w-[420px] flex-[2] basis-0">
                      <FieldLabelRow
                        label="Model"
                        required
                        help={{
                          title: 'Model',
                          description:
                            'Loads Anthropic model IDs for the selected provider on demand and stores the exact identifier used by the Claude SDK.',
                        }}
                      />
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        onOpenChange={(isOpen) => {
                          if (isOpen) ensureModelOptionsLoaded();
                        }}
                      >
                        <FormControl>
                          <SelectTrigger aria-label="Model" className="w-full">
                            <SelectValue placeholder="Select a model" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {availableModelOptions.map((option) => (
                            <SelectItem key={option.model_id} value={option.model_id}>
                              {option.model_id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!modelOptionsLoaded && !loadingModelOptions && !modelOptionsError && (
                        <p className="text-sm text-muted-foreground">
                          Click the model picker to load available {providerLabel} models.
                        </p>
                      )}
                      {loadingModelOptions && (
                        <p className="text-sm text-muted-foreground">Loading models...</p>
                      )}
                      {modelOptionsLoaded && modelOptions.length > 0 && (
                        <p className="text-sm text-muted-foreground">
                          Loaded {modelOptions.length} Anthropic model
                          {modelOptions.length === 1 ? '' : 's'} from{' '}
                          {modelOptions[0]?.region ?? providerLabel}.
                        </p>
                      )}
                      {modelOptionsLoaded && modelOptions.length === 0 && !modelOptionsError && (
                        <p className="text-sm text-muted-foreground">
                          No Anthropic models were returned for {providerLabel}.
                        </p>
                      )}
                      {modelOptionsError && (
                        <p className="text-sm text-destructive">{modelOptionsError}</p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="effort"
                  render={({ field }) => (
                    <FormItem className="flex-1 basis-0">
                      <FieldLabelRow
                        label="Effort"
                        required
                        help={{
                          title: 'Effort',
                          description:
                            'Reasoning depth. Higher = deeper analysis + longer latency. Admin-editable only.',
                        }}
                      />
                      <Select value={field.value} onValueChange={field.onChange} disabled={!isAdmin}>
                        <FormControl>
                          <SelectTrigger aria-label="Effort" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {EFFORT_OPTIONS.map((o) => (
                            <SelectItem key={o.id} value={o.id}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!isAdmin && <FormDescription>Admin-only</FormDescription>}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>}
              {isAdmin && !isOrchestrator && showHighEffortWarning && (
                <p className="text-sm text-status-warning">
                  ⚠︎ Higher effort levels increase response latency and cost.
                </p>
              )}

              {/* Row 3: Account Type | Development Status | Max Iterations */}
              <div className="flex items-end gap-4">
                <FormField
                  control={control}
                  name="accountType"
                  render={({ field }) => (
                    <FormItem className="flex-1 basis-0">
                      <FieldLabelRow
                        label="Account Type"
                        required
                        help={{
                          title: 'Account Type',
                          description:
                            'Controls which account-category tab the agent appears under in the selector.',
                        }}
                      />
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger aria-label="Account Type" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {ACCOUNT_TYPE_OPTIONS.map((option) => (
                            <SelectItem key={option.id} value={option.id}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="devStatus"
                  render={({ field }) => (
                    <FormItem className="flex-1 basis-0">
                      <FieldLabelRow
                        label="Development Status"
                        required
                        help={{
                          title: 'Development Status',
                          description:
                            'Controls catalog badges. Development agents are group-gated in local, DEV, and STG, and excluded from PRD.',
                        }}
                      />
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger aria-label="Development Status" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {DEV_STATUS_OPTIONS.map((option) => (
                            <SelectItem key={option.id} value={option.id}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {!isOrchestrator && (
                  <FormField
                    control={control}
                    name="maxIterations"
                    render={({ field }) => (
                      <FormItem className="flex-1 basis-0">
                        <FieldLabelRow
                          label="Max Iterations"
                          required
                          help={{
                            title: 'Max Iterations',
                            description: `Max tool-use loops before the agent stops (${MIN_ITERATIONS}–${MAX_ITERATIONS_CAP}). Admin-editable only.`,
                          }}
                        />
                        <FormControl>
                          <Input
                            type="number"
                            min={MIN_ITERATIONS}
                            max={MAX_ITERATIONS_CAP}
                            disabled={!isAdmin}
                            value={field.value}
                            onChange={(e) =>
                              field.onChange(e.target.value === '' ? NaN : e.target.valueAsNumber)
                            }
                          />
                        </FormControl>
                        {!isAdmin && <FormDescription>Admin-only</FormDescription>}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <FormField
                control={control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FieldLabelRow
                      label="Description"
                      required
                      help={{
                        title: 'Description',
                        description: 'Shown on the agent card and in the agent description panel.',
                      }}
                    />
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Controller
                control={control}
                name="examplePrompts"
                render={({ field, fieldState }) => (
                  <ListField
                    label="Example Prompts"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Type a starter prompt and press +"
                    isRequired
                    isInvalid={!!fieldState.error}
                    errorMessage={fieldState.error?.message}
                    contextualHelp={
                      <FieldHelp
                        title="Example Prompts"
                        description="Starter-prompt pills shown in the empty chat area."
                      />
                    }
                  />
                )}
              />

              <Controller
                control={control}
                name="bullets"
                render={({ field, fieldState }) => (
                  <ListField
                    label="Bullets"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Type an agent-card bullet and press +"
                    isRequired
                    isInvalid={!!fieldState.error}
                    errorMessage={fieldState.error?.message}
                    contextualHelp={
                      <FieldHelp title="Bullets" description="Capability bullet list on the agent card." />
                    }
                  />
                )}
              />

              <FormField
                control={control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FieldLabelRow
                      label="Tags (comma-separated, optional)"
                      help={{
                        title: 'Tags',
                        description: 'Free-text labels for search and discovery. Comma-separated.',
                      }}
                    />
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="changeSummary"
                render={({ field }) => (
                  <FormItem>
                    <FieldLabelRow
                      label="Change Summary (optional)"
                      help={{
                        title: 'Change Summary',
                        description: 'Stored with this version; visible in the version history sidebar.',
                      }}
                    />
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              {isAdmin && isLocal && (
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-bold">Runtime Source</p>
                  <p className="text-sm text-muted-foreground">
                    Local-only development toggle for switching between file-based manifest values and
                    published DB config.
                  </p>
                  <Controller
                    control={control}
                    name="useDbOverlay"
                    render={({ field }) => (
                      <div className="flex items-center gap-2">
                        <span className="text-sm">Use DB overlay</span>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          aria-label="Use DB overlay"
                          className="ml-auto"
                        />
                        <span className="text-sm text-muted-foreground">
                          {field.value ? 'On' : 'Off'}
                        </span>
                      </div>
                    )}
                  />
                  {!watch('useDbOverlay') && (
                    <p className="text-sm text-status-warning">
                      ⚠︎ Off — the Model, Effort, and Max Iterations fields above are saved but ignored at
                      runtime (and in eval comparisons); the agent keeps using the manifest YAML's values
                      until this is On.
                    </p>
                  )}
                </div>
              )}

              {hasErrors && (
                <p className="text-sm text-muted-foreground">
                  Please fix the highlighted required fields before publishing.
                </p>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={handleSaveDraft} disabled={savingDraft}>
                  {savingDraft ? 'Saving…' : 'Save Draft'}
                </Button>
                <Button
                  type="button"
                  onClick={onPublishClick}
                  disabled={savingDraft || publish.pending}
                >
                  {publish.pending ? 'Publishing…' : 'Publish'}
                </Button>
              </div>
            </div>
          </Form>

          <VersionHistorySidebar
            versions={versions}
            selectedId={selectedVersionId}
            onSelect={handleVersionSelect}
            onActivate={handleActivate}
            activating={activatingId !== null}
            onDelete={handleDeleteRequest}
            deleting={deleteVersion.pending}
          />
        </div>
      )}

      <PublishConfirmDialog
        isOpen={publish.showConfirm}
        onConfirm={publish.confirm}
        onCancel={publish.cancel}
        title="Publish configuration?"
      />
      <PublishConfirmDialog
        isOpen={deleteVersion.showConfirm}
        onConfirm={deleteVersion.confirm}
        onCancel={deleteVersion.cancel}
        title="Delete this draft?"
        description="This permanently removes the draft config version. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
      />
    </div>
  );
}
